/**
 * Masking for the filter history kept in localStorage.
 *
 * This is masking, not encryption, and is meant as such: it keeps
 * patient names out of plain sight in DevTools and makes one user's
 * history unreadable to another user of the same browser. The key is
 * built from the user's uuid and the build's own script name, both of
 * which are on the same machine, so it does not stand up to someone who
 * copies the browser profile. What protects the data is its lifetime:
 * the history is wiped on every sign-out and on the next sign-in.
 *
 * Plain JS rather than WebCrypto on purpose: `crypto.subtle` exists
 * only on secure origins, and the local dashboard runs on plain http.
 *
 * The scheme: a keystream from a seeded PRNG (sfc32), seeded from a hash
 * of key + a random per-write nonce, XORed over the UTF-8 JSON.
 */

const FORMAT = 'm1';

// cyrb128: four 32-bit hashes of a string, the seed for sfc32.
const cyrb128 = str => {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < str.length; i += 1) {
    const k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
};

const sfc32 = (a, b, c, d) => () => {
  a >>>= 0;
  b >>>= 0;
  c >>>= 0;
  d >>>= 0;
  const t = (((a + b) | 0) + d) | 0;
  d = (d + 1) | 0;
  a = b ^ (b >>> 9);
  b = (c + (c << 3)) | 0;
  c = (c << 21) | (c >>> 11);
  c = (c + t) | 0;
  return t >>> 0;
};

const xorStream = (bytes, seed) => {
  const next = sfc32(...cyrb128(seed));
  return bytes.map(byte => byte ^ (next() & 0xff));
};

const toBase64 = bytes => {
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const fromBase64 = text => Uint8Array.from(atob(text), char => char.charCodeAt(0));

/**
 * Names this build: the hashed main script in a production build
 * (`/static/js/main.<hash>.js`), so a deploy makes older history
 * unreadable and it is dropped; the dev server's `bundle.js` otherwise.
 */
export const buildTag = () => {
  if (typeof document === 'undefined') return 'build';
  const script = document.querySelector(
    'script[src*="/static/js/main."], script[src*="/static/js/bundle"]'
  );
  return script?.getAttribute('src') || 'build';
};

export const maskKey = userUuid => `${buildTag()}|${userUuid}`;

export const mask = (value, key) => {
  const nonce = Math.random().toString(36).slice(2, 12);
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  return `${FORMAT}.${nonce}.${toBase64(xorStream(bytes, `${key}|${nonce}`))}`;
};

/** The masked value, or null when it was masked with another key or is damaged. */
export const unmask = (text, key) => {
  try {
    const [format, nonce, body] = String(text).split('.');
    if (format !== FORMAT || !nonce || !body) return null;
    const bytes = xorStream(fromBase64(body), `${key}|${nonce}`);
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (error) {
    return null;
  }
};
